import { Link } from 'react-router-dom';
import {
  AtSign,
  BellRing,
  Bot,
  ChartGantt,
  Check,
  FileText,
  Gauge,
  KeyRound,
  Lock,
  MailCheck,
  Mic,
  PlugZap,
  Radar,
  Receipt,
  Search,
  Server,
  ShieldCheck,
  Signature,
  Smartphone,
  Sparkles,
  SquareKanban,
  Ticket,
  Vault,
} from 'lucide-react';
import { usePageMeta } from '../usePageMeta';
import { SectionHeading } from '../components/SectionHeading';
import { ImagePlaceholder } from '../components/ImagePlaceholder';
import { CtaBanner } from '../components/CtaBanner';

/** The background agent's scheduled runs, in the workspace's own timezone. */
const AGENT_RUNS: ReadonlyArray<readonly [when: string, name: string, does: string]> = [
  ['Nightly', 'Risk scan', 'Scores every active project 0–100 from overdue and stale tasks, budget burn, unanswered client comments and slipped deadlines.'],
  ['Every morning', 'Digest', 'Each teammate gets their overdue, due-today and this-week brief at the hour the studio picks.'],
  ['Nightly', 'Task triage', 'Flags unassigned and undated work — and in Act mode, assigns strays to the least-loaded person on the project.'],
  ['Mondays', 'Standup recap', 'A seven-day recap per project, written for the team from what actually happened.'],
];

/** Autonomy tiers — how far the agent is allowed to go on its own. */
const TIERS: ReadonlyArray<{ name: string; does: string; isDefault?: boolean }> = [
  { name: 'Observe', does: 'Records what it finds. Nobody is notified, nothing changes.' },
  { name: 'Suggest', does: 'Records and tells the people concerned what it would do.', isDefault: true },
  { name: 'Act', does: 'Also fixes what it safely can — every action in the audit log.' },
];

const WORK_TONES = ['g-violet', 'g-blue', 'g-pink', 'g-sun'] as const;

const WORK = [
  {
    icon: SquareKanban,
    title: 'Four views of one plan',
    body: 'A drag-and-drop Kanban board with per-project columns, a list, a timeline (Gantt) and a calendar — the same tasks, however the day needs them.',
  },
  {
    icon: ChartGantt,
    title: 'Structure that holds',
    body: 'Subtasks, dependencies, labels, priorities and recurring tasks, plus project templates so the next retainer starts where the last one worked.',
  },
  {
    icon: FileText,
    title: 'Docs next to the work',
    body: 'A Notion-style editor with a / menu — tables, callouts, to-dos, images, mentions — a doc tree, and version history you can restore from.',
  },
  {
    icon: AtSign,
    title: 'Task references everywhere',
    body: 'Every task has a number like TSK-12. @-mention people or tasks in any comment or doc, and the mention becomes a live link.',
  },
  {
    icon: Mic,
    title: 'Meetings, recorded both sides',
    body: 'Record a Meet, Zoom or Teams call from the doc — both sides, turn by turn — and get a labelled transcript and a summary written into the page.',
  },
  {
    icon: Sparkles,
    title: '/ai in every doc',
    body: 'Draft, rewrite and extract tasks from meeting notes without leaving the page. Dictation works in every text box, in the language you speak.',
  },
] as const;

/** Plans — priced by team size, not per seat, AI credits included. */
const PLANS: ReadonlyArray<{
  name: string;
  team: string;
  monthly: number;
  yearly: number;
  credits: string;
  featured?: boolean;
}> = [
  { name: 'Starter', team: 'Up to 5 people', monthly: 25, yearly: 250, credits: '$5 / month' },
  { name: 'Studio', team: '6–20 people', monthly: 50, yearly: 500, credits: '$10 / month', featured: true },
  { name: 'Agency', team: '21–40 people', monthly: 89, yearly: 800, credits: '$20 / month' },
];

export function Kinetix(): React.JSX.Element {
  usePageMeta(
    'Kinetix — AI-powered project management for creative agencies, by Inafana',
    'Kinetix runs projects, docs, proposals with eSign, time, invoices and a client portal for creative agencies — with a RAG assistant grounded in your own workspace and an autonomous agent that watches every project overnight.',
  );

  return (
    <>
      {/* ─────────────── hero ─────────────── */}
      <section className="product-hero">
        <div className="blob blob-violet" style={{ width: 580, height: 580, top: -170, left: '-6%' }} />
        <div className="blob blob-cyan" style={{ width: 440, height: 440, bottom: -120, right: '-4%' }} />
        <div className="container">
          <div className="product-hero-grid">
            <div className="product-hero-copy">
              <img className="product-mark" src="/assets/products/kinetix.svg" alt="Kinetix" />
              <h1 className="h1 reveal" data-delay="1">
                The agency workspace that{' '}
                <span className="grad">keeps watch overnight</span>.
              </h1>
              <p className="lede reveal" data-delay="2">
                Kinetix puts projects, docs, proposals, time, invoices and a client portal in
                one app built for creative studios — with an AI assistant that answers from
                your own work, and a background agent that scans every project for risk
                while the team sleeps.
              </p>
              <div className="hero-ctas reveal" data-delay="3">
                <Link to="/#contact" className="btn btn-primary btn-lg">
                  Get a Kinetix demo <span className="arrow">→</span>
                </Link>
                <Link to="/kinetix#pricing" className="btn btn-ghost btn-lg">
                  See pricing
                </Link>
              </div>
            </div>
            <div className="reveal" data-delay="2">
              <ImagePlaceholder
                label="Kinetix dashboard"
                hint="The dashboard in dark mode — project health scores, today's work, and the agent's latest findings."
                ratio="16 / 11"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────── assistant ─────────────── */}
      <section id="assistant" className="section-surface">
        <div className="container">
          <div className="feature-split">
            <div className="reveal">
              <h2 className="h2">
                An assistant that has <span className="si accent-text">read your projects</span>.
              </h2>
              <p className="lede">
                Ask what slipped last week, what the client said about the logo, or who is
                overloaded. Kinetix answers from your own docs, tasks, comments, proposals
                and audit log — and only from what the person asking is allowed to see.
              </p>
              <ul className="feature-list">
                <li>
                  <Search size={20} strokeWidth={1.8} />
                  <div>
                    <b>Grounded, not guessed</b>
                    <span>
                      Hybrid retrieval — full-text and vector search merged — over everything
                      the studio writes, re-indexed on every change.
                    </span>
                  </div>
                </li>
                <li>
                  <Bot size={20} strokeWidth={1.8} />
                  <div>
                    <b>Tools, not just answers</b>
                    <span>
                      It searches, reads the audit log, reports project status, creates tasks
                      and comments with real @-mentions — scoped to the asker's permissions.
                    </span>
                  </div>
                </li>
                <li>
                  <ShieldCheck size={20} strokeWidth={1.8} />
                  <div>
                    <b>Answers survive a closed tab</b>
                    <span>
                      A reply runs on the server. Lock the phone or lose signal mid-answer and
                      it is finished and waiting when you come back.
                    </span>
                  </div>
                </li>
                <li>
                  <PlugZap size={20} strokeWidth={1.8} />
                  <div>
                    <b>Reachable from Claude and your IDE</b>
                    <span>
                      A built-in MCP server over OAuth lets an outside AI client use the same
                      tools, with read-only or read-write consent per connection.
                    </span>
                  </div>
                </li>
              </ul>
            </div>
            <div className="reveal" data-delay="1">
              <ImagePlaceholder
                label="Assistant answering with sources"
                hint="A question about a client project, the streamed answer, and the docs and tasks it cited."
                ratio="4 / 5"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────── autonomous agent ─────────────── */}
      <section id="agent">
        <div className="container">
          <SectionHeading
            anim="blur"
            title={
              <>
                An agent on the <span className="si accent-text">night shift</span>.
              </>
            }
            lede="A scheduler inside Kinetix runs the checks a good producer would, every day, in your studio's timezone — and you decide how much it is allowed to do about what it finds."
          />

          <div className="kx-runs">
            {AGENT_RUNS.map(([when, name, does], i) => (
              <article key={name} className="kx-run reveal" data-delay={(i % 4) + 1}>
                <span className="kx-run-when">{when}</span>
                <h3 className="h3">{name}</h3>
                <p>{does}</p>
              </article>
            ))}
          </div>

          <div className="kx-tiers reveal" data-delay="1">
            <div className="kx-tiers-head">
              <Radar size={18} strokeWidth={1.8} aria-hidden="true" />
              <span>Autonomy, set per workspace</span>
            </div>
            <ol className="kx-tiers-list">
              {TIERS.map((t) => (
                <li key={t.name} className={t.isDefault ? 'is-default' : undefined}>
                  <b>
                    {t.name}
                    {t.isDefault && <em>default</em>}
                  </b>
                  <span>{t.does}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ─────────────── projects, tasks, docs ─────────────── */}
      <section className="section-surface">
        <div className="container">
          <SectionHeading
            anim="wipe"
            title={
              <>
                The work itself, <span className="si accent-text">in one place</span>.
              </>
            }
            lede="Boards, timelines, docs and meeting notes live against the project they belong to — so the assistant, the agent and the next person to join can all find them."
          />

          <div className="kx-shot reveal">
            <ImagePlaceholder
              label="Project board beside a doc"
              hint="Left: the Kanban board with TSK references on the cards. Right: a doc with a recorded meeting, its summary and the Transcript button."
              ratio="16 / 8"
            />
          </div>

          <div className="capability-grid kx-gap">
            {WORK.map((w, i) => (
              <article key={w.title} className="module-card reveal" data-delay={(i % 3) + 1}>
                <div className={`icon ${WORK_TONES[i % WORK_TONES.length]}`} aria-hidden="true">
                  <w.icon size={20} strokeWidth={1.8} />
                </div>
                <h3 className="h3">{w.title}</h3>
                <p>{w.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────── client portal ─────────────── */}
      <section>
        <div className="container">
          <div className="feature-split">
            <div className="reveal">
              <ImagePlaceholder
                label="Client portal"
                hint="What the client sees — their project's client-visible tasks, a deliverable awaiting approval, and an open request."
                ratio="4 / 3"
              />
            </div>
            <div className="reveal" data-delay="1">
              <h2 className="h2">
                Clients see the work — <span className="si accent-text">only theirs</span>.
              </h2>
              <p className="lede">
                Every comment, task and doc is marked internal or client-visible. The portal
                shows a client the client-visible work of their own projects and nothing
                else, enforced in the data layer rather than hidden in the interface.
              </p>
              <ul className="feature-list">
                <li>
                  <Check size={20} strokeWidth={1.8} />
                  <div>
                    <b>Approvals the client actually gives</b>
                    <span>
                      The studio requests sign-off; the client approves or sends it back, and
                      every revision is counted.
                    </span>
                  </div>
                </li>
                <li>
                  <Ticket size={20} strokeWidth={1.8} />
                  <div>
                    <b>Requests with a thread</b>
                    <span>
                      Clients raise tickets in the portal. The team replies in the same
                      thread, with internal notes the client never sees.
                    </span>
                  </div>
                </li>
                <li>
                  <MailCheck size={20} strokeWidth={1.8} />
                  <div>
                    <b>No password to forget</b>
                    <span>
                      Send a client an access link; they get a six-digit code by email and
                      they are in. Revoke the link and every session it opened ends.
                    </span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────── proposals & money ─────────────── */}
      <section className="section-surface">
        <div className="container">
          <SectionHeading
            anim="zoom"
            title={
              <>
                From the pitch <span className="si accent-text">to the paid invoice</span>.
              </>
            }
            lede="The proposal that wins the work becomes the project with one click, and the hours logged against it become the invoice — without re-keying anything in between."
          />
          <div className="kx-duo">
            <article className="kx-duo-card reveal" data-delay="1">
              <div className="kx-duo-top">
                <div className="icon g-violet" aria-hidden="true">
                  <Signature size={20} strokeWidth={1.8} />
                </div>
                <h3 className="h3">Proposals &amp; eSign</h3>
              </div>
              <ImagePlaceholder
                label="Proposal builder with the AI co-writer"
                hint="The co-writer's interview on one side, the drafted proposal with structured pricing on the other."
                ratio="16 / 10"
              />
              <ul>
                <li>An AI co-writer that interviews you, drafts, then revises section by section</li>
                <li>Grounded in the client's history, with version history on every section</li>
                <li>Structured pricing, and a content hash that freezes what was sent</li>
                <li>A public signing page — typed or drawn — with a full event trail</li>
                <li>A signed PDF with its certificate, and one-click convert to project</li>
              </ul>
            </article>

            <article className="kx-duo-card reveal" data-delay="2">
              <div className="kx-duo-top">
                <div className="icon g-sun" aria-hidden="true">
                  <Receipt size={20} strokeWidth={1.8} />
                </div>
                <h3 className="h3">Time, money &amp; reports</h3>
              </div>
              <ImagePlaceholder
                label="Profitability and a client report"
                hint="The budget and profitability chart for a project, beside the client-facing progress report."
                ratio="16 / 10"
              />
              <ul>
                <li>Timers and time entries, with the rate captured at the time</li>
                <li>Expenses, budgets and profitability charts per project</li>
                <li>Invoices generated from unbilled time, with partial payments</li>
                <li>Client progress reports with computed metrics and an AI-written narrative</li>
                <li>Published to the portal or a share link, and exported as PDF</li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      {/* ─────────────── platform ─────────────── */}
      <section>
        <div className="container">
          <SectionHeading
            anim="slide"
            title={
              <>
                Built for studios, <span className="si accent-text">run as a service</span>.
              </>
            }
            lede="Every agency gets its own isolated workspace — nothing shared with any other tenant. Or run the same build on your own server."
          />
          <ul className="feature-list kx-inline-list reveal" data-delay="1">
            <li>
              <Vault size={20} strokeWidth={1.8} />
              <div>
                <b>My Vault</b>
                <span>A private space per person for to-dos, notes, reminders and files — searchable by its owner, invisible to everyone else.</span>
              </div>
            </li>
            <li>
              <Smartphone size={20} strokeWidth={1.8} />
              <div>
                <b>Web, PWA and Android</b>
                <span>Installable on any device, plus an Android app with push notifications that open the right page.</span>
              </div>
            </li>
            <li>
              <BellRing size={20} strokeWidth={1.8} />
              <div>
                <b>Live, everywhere</b>
                <span>Realtime updates across tabs and people, a notifications inbox, and a full audit log of every change.</span>
              </div>
            </li>
            <li>
              <KeyRound size={20} strokeWidth={1.8} />
              <div>
                <b>Invite-only by design</b>
                <span>No public signup. The studio owner invites the team; clients come in through their own access links.</span>
              </div>
            </li>
            <li>
              <Lock size={20} strokeWidth={1.8} />
              <div>
                <b>Tenant isolation in the data layer</b>
                <span>Every read is scoped to the workspace, and AI retrieval filters by workspace and visibility before anything else.</span>
              </div>
            </li>
            <li>
              <Gauge size={20} strokeWidth={1.8} />
              <div>
                <b>AI spend you can see</b>
                <span>Usage is metered per workspace and broken down by feature, model and person on the AI usage page.</span>
              </div>
            </li>
          </ul>
        </div>
      </section>

      {/* ─────────────── pricing ─────────────── */}
      <section id="pricing" className="section-surface">
        <div className="container">
          <SectionHeading
            anim="drop"
            title={
              <>
                Priced by team size, <span className="si accent-text">not per seat</span>.
              </>
            }
            lede="Every plan includes every feature and AI credits every month. Billed in USD — pay yearly and get two months or more free."
          />
          <div className="kx-plans">
            {PLANS.map((p, i) => (
              <article
                key={p.name}
                className={`kx-plan reveal${p.featured ? ' is-featured' : ''}`}
                data-delay={i + 1}
              >
                {p.featured && <span className="kx-plan-flag">Most studios</span>}
                <h3 className="h3">{p.name}</h3>
                <span className="kx-plan-team">{p.team}</span>
                <p className="kx-plan-price">
                  <b>${p.monthly}</b>
                  <span>/ month</span>
                </p>
                <span className="kx-plan-yearly">or ${p.yearly} / year</span>
                <ul>
                  <li>
                    <Check size={16} strokeWidth={2.4} aria-hidden="true" />
                    {p.credits} AI credits included
                  </li>
                  <li>
                    <Check size={16} strokeWidth={2.4} aria-hidden="true" />
                    Client portal, assistant and agent
                  </li>
                  <li>
                    <Check size={16} strokeWidth={2.4} aria-hidden="true" />
                    Web, PWA and Android
                  </li>
                </ul>
              </article>
            ))}
          </div>

          <div className="kx-selfhost reveal" data-delay="1">
            <div className="kx-selfhost-icon g-deep" aria-hidden="true">
              <Server size={20} strokeWidth={1.8} />
            </div>
            <div>
              <b>Self-hosted licence</b>
              <span>
                The same build on your own server: <strong>$250 once + $10 / month</strong>{' '}
                with a server we supply, or <strong>$750 once</strong> if you bring your own.
              </span>
            </div>
          </div>
        </div>
      </section>

      <CtaBanner
        title={
          <>
            See Kinetix run <span className="si">your studio</span>.
          </>
        }
        lede="Tell us how your agency plans work, wins it, and reports to clients today. We'll set up a workspace and walk you through the same week inside Kinetix — agent included."
        primaryLabel="Get a Kinetix demo"
      />
    </>
  );
}
